/**
 * HTTP Client Utility with JWT Token Management
 * Handles API requests with automatic token refresh
 */

// Use nullish coalescing to handle empty strings correctly
// Empty string enables Vite proxy for local development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://dev.services.leadconnectorhq.com/hltestmanager'

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const REMEMBER_ME_KEY = 'remember_me'

// Determine which storage to use
function getStorage() {
  // Always check localStorage for the remember_me flag
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY)
  // If remember_me is explicitly false, use sessionStorage; otherwise default to localStorage
  return rememberMe === 'false' ? sessionStorage : localStorage
}

// Token refresh interval (check every 5 minutes, refresh 2 minutes before expiry)
let tokenRefreshInterval = null
let isRefreshing = false

/**
 * Get access token from appropriate storage
 */
export function getAccessToken() {
  const storage = getStorage()
  return storage.getItem(ACCESS_TOKEN_KEY)
}

/**
 * Get refresh token from appropriate storage
 */
export function getRefreshToken() {
  const storage = getStorage()
  return storage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * Store tokens in appropriate storage based on rememberMe preference
 */
export function storeTokens(accessToken, refreshToken, rememberMe = true) {
  // Always store the remember_me preference in localStorage
  localStorage.setItem(REMEMBER_ME_KEY, String(rememberMe))
  
  // Choose storage based on preference
  const storage = rememberMe ? localStorage : sessionStorage
  
  if (accessToken) {
    storage.setItem(ACCESS_TOKEN_KEY, accessToken)
  }
  if (refreshToken) {
    storage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

/**
 * Clear all tokens from both storages
 */
export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  sessionStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  sessionStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem('user_session')
  sessionStorage.removeItem('user_session')
  // Keep remember_me preference so we know what storage to use on next login
}

/**
 * Decode JWT token to check expiration
 */
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))
    return JSON.parse(jsonPayload)
  } catch (e) {
    return null
  }
}

/**
 * Check if token is expired or expiring soon (within 2 minutes)
 */
function isTokenExpiringSoon(token) {
  if (!token) return true
  
  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true
  
  // Token is expiring if it expires within 2 minutes
  const expirationTime = decoded.exp * 1000 // Convert to milliseconds
  const currentTime = Date.now()
  const timeUntilExpiry = expirationTime - currentTime
  
  return timeUntilExpiry < 120000 // 2 minutes
}

/**
 * Refresh access token using refresh token
 */
async function refreshAccessToken() {
  if (isRefreshing) {
    // If already refreshing, wait and return current token
    return getAccessToken()
  }
  
  const refreshToken = getRefreshToken()
  
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  isRefreshing = true

  try {
    // Handle empty API_BASE_URL for local dev (use Vite proxy)
    const refreshUrl = !API_BASE_URL || API_BASE_URL === '' 
      ? '/hltestmanager/auth/refresh'
      : `${API_BASE_URL}/auth/refresh`
    
    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken })
    })

    if (!response.ok) {
      throw new Error('Token refresh failed')
    }

    const data = await response.json()
    
    // Store new access token
    storeTokens(data.accessToken, null)
    
    return data.accessToken
  } catch (error) {
    // If refresh fails, clear all tokens and force re-login
    clearTokens()
    throw error
  } finally {
    isRefreshing = false
  }
}

/**
 * Start automatic token refresh
 * Uses a hybrid approach: setTimeout for next refresh + on-demand checking
 */
export function startTokenRefresh() {
  // Clear any existing timeout
  if (tokenRefreshInterval) {
    clearTimeout(tokenRefreshInterval)
    tokenRefreshInterval = null
  }
  
  scheduleNextRefresh()
}

/**
 * Schedule the next token refresh based on token expiration
 */
function scheduleNextRefresh() {
  const accessToken = getAccessToken()
  
  if (!accessToken) {
    return
  }
  
  const decoded = decodeToken(accessToken)
  if (!decoded || !decoded.exp) {
    return
  }
  
  // Calculate when to refresh (2 minutes before expiry)
  const expirationTime = decoded.exp * 1000
  const refreshTime = expirationTime - 120000 // 2 minutes before expiry
  const currentTime = Date.now()
  const timeUntilRefresh = refreshTime - currentTime
  
  // If token is already expired or expiring very soon, refresh immediately
  if (timeUntilRefresh <= 0) {
    refreshAccessToken().then(() => {
      // After refresh, schedule the next one
      scheduleNextRefresh()
    }).catch(() => {
      // If refresh fails, stop trying
      stopTokenRefresh()
    })
    return
  }
  
  // Schedule refresh at the calculated time
  tokenRefreshInterval = setTimeout(async () => {
    try {
      await refreshAccessToken()
      // After successful refresh, schedule the next one
      scheduleNextRefresh()
    } catch (error) {
      // If refresh fails, stop trying
      stopTokenRefresh()
    }
  }, timeUntilRefresh)
}

/**
 * Stop automatic token refresh
 */
export function stopTokenRefresh() {
  if (tokenRefreshInterval) {
    clearTimeout(tokenRefreshInterval)
    tokenRefreshInterval = null
  }
}

/**
 * Make an authenticated API request
 * Automatically adds Authorization header and handles token refresh
 */
export async function apiRequest(endpoint: string, options: { headers?: Record<string, string>; [key: string]: any } = {}) {
  // If API_BASE_URL is empty (local dev with proxy), add /hltestmanager prefix
  // Otherwise use the full API_BASE_URL
  let url: string
  if (endpoint.startsWith('http')) {
    url = endpoint
  } else if (!API_BASE_URL || API_BASE_URL === '') {
    // Local development: use relative URL with /hltestmanager prefix for Vite proxy
    url = `/hltestmanager${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`
  } else {
    // Production: use full API_BASE_URL
    url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`
  }
  
  // Prepare headers
  // Don't set Content-Type for FormData - browser will set it with boundary
  const isFormData = options.body instanceof FormData
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers || {})
  }
  
  // Remove Content-Type from headers if it's FormData (browser will set it)
  if (isFormData && headers['Content-Type']) {
    delete headers['Content-Type']
  }

  // Add Authorization header if token exists (except for auth endpoints)
  if (!endpoint.includes('/auth/login') && !endpoint.includes('/auth/register')) {
    let accessToken = getAccessToken()
    
    // Proactively refresh if token is expiring soon (< 2 minutes)
    if (accessToken && isTokenExpiringSoon(accessToken) && getRefreshToken()) {
      try {
        accessToken = await refreshAccessToken()
      } catch (error) {
        // If refresh fails, continue with current token and let 401 handler deal with it
        console.warn('Proactive token refresh failed:', error)
      }
    }
    
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }
  }

  // Merge options
  const fetchOptions = {
    ...options,
    headers
  }

  try {
    // Make the request
    let response = await fetch(url, fetchOptions)

    // If 401 and we have a refresh token, try to refresh and retry
    if (response.status === 401) {
      if (getRefreshToken()) {
        try {
          // Try to refresh the token
          const newAccessToken = await refreshAccessToken()
          
          // Retry the original request with new token
          headers['Authorization'] = `Bearer ${newAccessToken}`
          fetchOptions.headers = headers
          response = await fetch(url, fetchOptions)
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          // Store current URL for redirect after login
          const currentPath = window.location.pathname + window.location.search
          if (currentPath && currentPath !== '/login' && currentPath !== '/') {
            localStorage.setItem('redirect_after_login', currentPath)
          }
          clearTokens()
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
          throw new Error('Session expired. Please login again.')
        }
      } else {
        // No refresh token available, store URL and redirect to login
        const currentPath = window.location.pathname + window.location.search
        if (currentPath && currentPath !== '/login' && currentPath !== '/') {
          localStorage.setItem('redirect_after_login', currentPath)
        }
        clearTokens()
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        throw new Error('Session expired. Please login again.')
      }
    }

    // Parse response
    let data
    try {
      data = await response.json()
    } catch (parseError) {
      // If JSON parsing fails (common for 413 errors), create error with status-specific message
      let errorMessage = `Request failed with status ${response.status}`
      if (response.status === 413) {
        errorMessage = 'Request payload too large. The data exceeds 25MB limit. Please split your import into smaller batches.'
      } else if (response.status === 400) {
        errorMessage = 'Invalid request data. Please check your input and try again.'
      } else if (response.status === 500) {
        errorMessage = 'Server error occurred. Please try again or contact support.'
      }
      
      const error = new Error(errorMessage) as any
      error.status = response.status
      throw error
    }

    // Handle errors
    if (!response.ok) {
      // Handle specific HTTP status codes with user-friendly messages
      if (response.status === 413) {
        const error = new Error('Request payload too large. The data exceeds 25MB limit. Please split your import into smaller batches.') as any
        error.status = 413
        error.code = 'PAYLOAD_TOO_LARGE'
        error.details = data
        throw error
      }
      
      // Handle NestJS error format (error nested in 'error' object)
      const errorData = data.error || data
      const errorMessage = errorData.message || data.message || 'Request failed'
      
      const error = new Error(errorMessage) as any
      error.code = errorData.code || data.code
      error.status = response.status
      error.details = data
      throw error
    }

    return data
  } catch (error) {
    // Re-throw with additional context
    if (error.message === 'Failed to fetch') {
      throw new Error('Cannot connect to server. Please check your connection.')
    }
    throw error
  }
}

/**
 * Convenience methods for different HTTP verbs
 */
export const http = {
  get: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint, data, options = {}) => {
    // If data is FormData, pass it directly without stringifying
    const body = data instanceof FormData ? data : JSON.stringify(data)
    return apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body
    })
  },
  
  put: (endpoint, data, options = {}) => apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  patch: (endpoint, data, options = {}) => apiRequest(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data)
  }),
  
  delete: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'DELETE' })
}

export default http

